import type { ClipboardEvent } from 'react';
import TurndownService from 'turndown';

const turndown = new TurndownService({ bulletListMarker: '-', codeBlockStyle: 'fenced' });

export function handleMarkdownCopy(event: ClipboardEvent<HTMLDivElement>, markdown: string) {
  if (event.defaultPrevented) {
    return;
  }

  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return;
  }

  const ranges = Array.from({ length: selection.rangeCount }, (_, index) => {
    return selection.getRangeAt(index);
  });
  if (ranges.some((range) => !event.currentTarget.contains(range.commonAncestorContainer))) {
    return;
  }

  const selectedText = selection.toString().trim();
  // `innerText` matches the rendered selection and excludes Streamdown's hidden controls.
  // eslint-disable-next-line unicorn/prefer-dom-node-text-content
  const fullText = (event.currentTarget.innerText ?? event.currentTarget.textContent)?.trim();
  let copiedMarkdown = markdown;

  if (selectedText !== fullText) {
    const container = document.createElement('div');
    ranges.forEach((range) => {
      const fragment = range.cloneContents();
      const commonAncestor =
        range.commonAncestorContainer instanceof HTMLElement
          ? range.commonAncestorContainer
          : range.commonAncestorContainer.parentElement;

      let wrappedContent: Node = fragment;
      let ancestor = commonAncestor;

      while (ancestor && ancestor !== event.currentTarget) {
        const wrapper = ancestor.cloneNode() as HTMLElement;
        if (ancestor instanceof HTMLOListElement && wrapper instanceof HTMLOListElement) {
          const selectedItemIndex = Array.from(ancestor.children).findIndex(
            (item) => item === range.startContainer || item.contains(range.startContainer),
          );
          if (selectedItemIndex > 0) {
            wrapper.start = ancestor.start + selectedItemIndex;
          }
        }
        wrapper.appendChild(wrappedContent);
        wrappedContent = wrapper;
        ancestor = ancestor.parentElement;
      }

      container.appendChild(wrappedContent);
    });
    container
      .querySelectorAll('[data-streamdown="code-block-header"]')
      .forEach((element) => element.remove());
    copiedMarkdown = turndown.turndown(container).trim();
  }

  if (!copiedMarkdown) {
    return;
  }

  event.preventDefault();
  event.clipboardData.setData('text/plain', copiedMarkdown);
  event.clipboardData.setData('text/markdown', copiedMarkdown);
}
